import * as React from "react";
import { cx } from "utils/join-classnames";
import { IconNames } from "core/models";
import { Tooltip, Spinner, Icon } from "ui/components";
import styles from "./Button.styles.module.scss";

interface Props {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    disabled?: boolean;
    type: "primary" | "secondary" | "icon" | "function";
    theme?: "dark" | "light";
    as?: string | React.ComponentType<any>;
    tooltip?: string;
    icon?: IconNames;
    active?: boolean;
    float?: boolean;
    [key: string]: any;
    size?: "small" | "medium" | "large";
    onRed?: boolean;
    pending?: boolean;
    action?: "submit" | "reset" | "button";
    onHover?(state: boolean): void;
    children?: React.ReactNode;
}

const Button: React.FC<Props> = ({
    as: ElementType = "button",
    className,
    disabled,
    onClick,
    children,
    theme = "light",
    type,
    icon,
    tooltip = "",
    active,
    float = false,
    size = "medium",
    onRed = false,
    pending = false,
    action = "button",
    onHover,
}: Props) => {
    const classes = cx(
        styles.button,
        active ? styles.is__active : null,
        disabled ? styles.is__disabled : null,
        type === "primary" ? styles.primary : null,
        theme === "dark" && type === "secondary"
            ? styles.secondary__dark
            : null,
        theme === "light" && type === "secondary"
            ? styles.secondary__light
            : null,
        theme === "dark" && type === "icon" ? styles.icon__dark : null,
        theme === "light" && type === "icon" ? styles.icon__light : null,
        theme === "dark" && type === "function" ? styles.function__dark : null,
        theme === "light" && type === "function"
            ? styles.function__light
            : null,
        theme === "light" && type === "function" && onRed
            ? styles.function__light__onred
            : null,
        theme === "dark" && type === "function" && size === "large"
            ? styles.function__dark__large
            : null,
        theme === "light" && type === "function" && size === "large"
            ? styles.function__light__large
            : null,
        theme === "dark" && type === "function" && size === "small"
            ? styles.function__dark__small
            : null,
        theme === "light" && type === "function" && size === "small"
            ? styles.function__light__small
            : null,
        float === true ? styles.float : null,
        className,
    );

    const ButtonEl = (
        // eslint-disable-next-line react/button-has-type
        <button
            // eslint-disable-next-line react/button-has-type
            type={action}
            className={classes}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            onMouseEnter={() => onHover && onHover(true)}
            onMouseLeave={() => onHover && onHover(false)}
        >
            <span className={styles.content}>
                {!pending && icon && (
                    <Icon className={styles.icon} name={icon} />
                )}
                {pending && <Spinner size="small" className={styles.spinner} />}
                <span className={cx(icon && styles.buttonContent)}>
                    {children}
                </span>
            </span>
        </button>
    );

    const tooltipDisabled = true && tooltip === "";

    if (ElementType === "button" && tooltipDisabled === true) {
        return ButtonEl;
    }
    if (ElementType === "button" && tooltipDisabled === false) {
        return (
            <Tooltip text={tooltip} disabled={tooltipDisabled}>
                {ButtonEl}
            </Tooltip>
        );
    }
    return (
        <ElementType
            className={classes}
            onClick={disabled ? null : onClick}
            type={action}
            disabled={disabled}
            onMouseEnter={() => onHover && onHover(true)}
            onMouseLeave={() => onHover && onHover(false)}
        >
            {children}
        </ElementType>
    );
};

export default React.memo(Button);
